import {AfterContentChecked, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {NeqRangeModel} from '@sharedcommon/model/range.model';
import {NeqTableConvertFilter, NeqTableRequest} from './neq-table-request.model';
import {Constants} from '@sharedcommon/util/constants.util';
import {PrimengTableCols} from '@sharedcommon/interfaces/primeng-table-cols';
import {PrimengRowActionModel} from '@sharedcommon/model/primeng-row-action.model';
import {PrimengTableVariableRequirementModel} from '@sharedcommon/model/primeng-table-variable-requirement.model';
import {NeqTableData} from '@sharedcommon/model/neq-table-data.model';
import {PrimengLazyEventModel} from '@sharedcommon/model/primeng-lazy-event.model';
import {UserService} from '@sharedcommon/services/user.service';
import { Observable } from 'rxjs';
import { RawMaterialDtoExtend } from '@root/user/src/app/view/neq/pages/rawmaterials/rawmaterials-list.page';

@Component({
  selector: 'neq-table',
  templateUrl: 'neq-table.component.html',
  host: {'class': 'asi-component asi-table neq-table'}
})
export class NeqTableComponent<T> implements OnInit, AfterContentChecked  {

  /**
   * Enable or disable the row selection
   */
  @Input() selectableRows = false;
  @Input() nutrients: {};
  @Input() activeFilter: boolean;
  @Input() autoSort = false;
  @Input() autoPaginate = false;
  @Input() rowClass: (data:T) => string;
  @Input() changePageOnTop = false;
  @Input() cols: PrimengTableCols[];
  @Input() onRequestData: (request: NeqTableRequest) => Promise<NeqTableData<T>>;

  /**
   * Function to enable/disbale the checkbox which is present if this is a selectable row
   * @NB this is selectable if the attribute selectableRows is at true
   */
  @Input() disableSelectableCell: (rowData:T) => boolean ;

  /**
   * Unique session Id to mangage the store of filter/pagination/sort
   */
  @Input() sessionId: string;

  /**
   * Active or disable the action on the lines
   */
  @Input() actions: 'user' | PrimengRowActionModel[];

  /**
   * All requirement for the Variable column functionality
   */
  @Input() dynamicRequirements: PrimengTableVariableRequirementModel;

  /**
   * At true if you do want any pagination and load all
   */
  @Input() noPagination: boolean = false;

  /**
   * Set or not the lazy mode on the table
   * The lazy mode apply the filter, sort and pagination on backend API
   */
  @Input() lazy = true;

  /**
   * Number of dynamic column present
   */
  @Input() numberDynamicColumn = 0;

  /**
   * Event when code/entity unicity checked
   */
  @Input() eventDataChecked: Observable<void>;

  /**
   * Is on loading
   */
  @Input() loading: boolean = false;

  /**
   * Inital RawMaterial Data
   */
  @Input() initalRawMaterialsData: RawMaterialDtoExtend[];


  /**
   * Nutrients data for RawMaterial
   */
  @Input() nutrientsData: any[];

  /**
   * Initial nutirents data for RawMaterial
   */
  @Input() initNutrientsData: any[];

  /**
   * Total number of rows of the table
   */
  totalRecords = 0;

  /**
   * Table state
   */
  lazyTableEvent: PrimengLazyEventModel;


  data = new NeqTableData<T>();
  rangeModel: NeqRangeModel;
  allData: Array<T>;

  constructor(
    private changeDetector :ChangeDetectorRef,
    private readonly userService: UserService
  ) {
  }

  ngOnInit() {
    this.rangeModel = new NeqRangeModel();
    this.rangeModel.ranges = Constants.ARRAY_NB_ROW_BY_PAGE;

    if(!this.lazy) {
      this.fireRefresh();
    }
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges() ;
  }

  public updateData(data: NeqTableData<T>) {
    this.data = data;
    this.totalRecords = data.totalElements;
  }

  requestData(lazyEvent: PrimengLazyEventModel) {

    this.lazyTableEvent = lazyEvent;
    if (this.data) {
      this.data.results = [];
    }
    if (this.onRequestData) {
      let loadingMustBeChanged = !this.loading;

      if (loadingMustBeChanged) {
        this.loading = true;
      }

      Promise.resolve(this.requestDataInObservableMode())
        .then((data: NeqTableData<T>) => {
          this.updateData(data);
        })
        .finally(() => {
          if (loadingMustBeChanged) {
            this.loading = false;
          }
        });
    }
  }

  /**
   * Handle the request data in observable mode to get the buil-in popin in case of error
   * @returns the promise
   */
  private requestDataInObservableMode(): Promise<NeqTableData<T>> {
    const request = this.getNeqTableRequest();
    return this.onRequestData(request);
  }

  fireRefresh() {
    this.requestData(this.lazyTableEvent);
  }

  getNeqTableRequest(): NeqTableRequest {
    const request: NeqTableRequest = new NeqTableRequest();
    if (this.noPagination) {
      request.pageIndex = Constants.ALL_PAGE ;
      request.nbElementsParPage = 1 ;
    } else {
      request.pageIndex = (this.lazyTableEvent && this.lazyTableEvent.first > this.lazyTableEvent.rows - 1 ) ?
                                      (this.lazyTableEvent.first / this.lazyTableEvent.rows) + 1
                                      : 1;

      const numberPerPage = (this.userService.getMetaValue('nb-row-by-page')) ?
        this.userService.getMetaValue('nb-row-by-page') : 10;
      request.nbElementsParPage = (this.lazyTableEvent) ? this.lazyTableEvent.rows : +numberPerPage;
    }
    request.filters = (this.lazyTableEvent) ? this.convertPrimengFilter(this.lazyTableEvent.filters) : null;
    request.tri = [];
    if (this.lazyTableEvent?.multiSortMeta?.length > 0) {
      this.lazyTableEvent.multiSortMeta.forEach((fieldSort: {field: string, order: number}) => {
        request.tri.push({field: fieldSort.field, asc: (fieldSort.order === 1)});
      });
    }
    return request;
  }

  /**
   * Convert primeng filter object to be used by the MatrixNutri Backend
   *
   * @param filters
   */
  private convertPrimengFilter(filters: any): NeqTableConvertFilter[] {
    const filtersKey = Object.keys(filters);
    const backFilters: NeqTableConvertFilter[] = [];
    filtersKey.forEach((key: string) => {
      if (filters?.[key]?.[0]?.value != null) {
        backFilters.push({value: filters[key][0].value, key});
      }
    });
    return backFilters;
  }
}
